import { ScrollView, StyleSheet, View } from "react-native";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { WorkspaceHeader } from "../components/chatNewInterface/interfaceComponents/workspaceHeader/WorkspaceHeader";
import TabNavigator from "../components/common/general/TabNavigator/TabNavigator";
import { useAppConfig } from "../AppConfigProvider";
import CompanionILECardList from "../components/chat-companion/CompanionILECardList/CompanionILECardList";
import CompanionLayout from "../layouts/CompanionLayout";
import type { CompanionILECardProps } from "../components/chat-companion/CompanionILECard/CompanionILECard";
import type { Objective } from "../components/chat-companion/CompanionILECard/CompanionILECard";
import type { RootStackParamList } from "../navigation/navigation.types";

// Test data - will be replaced with actual GraphQL query later
const mockObjectives: Objective[] = [
  {
    id: "1",
    objective: "Understand the behavioral perspective and learning theories",
    sequence: 1,
    status: "pending",
  },
  {
    id: "2",
    objective: "Explore the cognitive approach to mental processes",
    sequence: 2,
    status: "pending",
  },
  {
    id: "3",
    objective: "Examine biological foundations of behavior",
    sequence: 3,
    status: "pending",
  },
];

const mockObjectivesWithProgress: Objective[] = [
  {
    id: "1",
    objective: "Solve linear equations using algebraic methods",
    sequence: 1,
    status: "completed",
  },
  {
    id: "2",
    objective: "Convert between different forms of linear equations",
    sequence: 2,
    status: "completed",
  },
  {
    id: "3",
    objective: "Graph linear equations on the coordinate plane",
    sequence: 3,
    status: "pending",
  },
  {
    id: "4",
    objective: "Identify slope and y-intercept from equations",
    sequence: 4,
    status: "pending",
  },
  {
    id: "5",
    objective: "Apply linear equations to real-world problems",
    sequence: 5,
    status: "pending",
  },
];

const testActivities: CompanionILECardProps[] = [
  {
    id: "1",
    title: "Major Perspectives in Psychology",
    courseName: "Introduction to Psychology",
    created: new Date(Date.now() - 35 * 60 * 1000),
    description: "Dive deep into the five major psychological perspectives and learn how to apply each...",
    objectives: mockObjectives,
    status: "not_started",
    onResetProgress: () => console.log("Reset progress 1"),
    onDeleteActivity: () => console.log("Delete activity 1"),
  },
  {
    id: "2",
    title: "Linear Equations and Graphing",
    courseName: "College Algebra",
    created: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    description: "Master the fundamentals of linear equations, including slope-intercept form, point-slope form,...",
    objectives: mockObjectivesWithProgress,
    status: "in_progress",
    onResetProgress: () => console.log("Reset progress 2"),
    onDeleteActivity: () => console.log("Delete activity 2"),
  },
  {
    id: "3",
    title: "Psychology Flashcards: Perspectives & Key Concepts",
    courseName: "Introduction to Psychology",
    created: new Date(Date.now() - 1 * 60 * 60 * 1000),
    description: "Master key psychology terms and concepts through spaced repetition. Review behavioral, cognitive,...",
    objectives: mockObjectives,
    status: "not_started",
    onResetProgress: () => console.log("Reset progress 3"),
    onDeleteActivity: () => console.log("Delete activity 3"),
  },
  {
    id: "4",
    title: "Photosynthesis and Cellular Respiration",
    courseName: "Introduction to Biology",
    created: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    description:
      "Explore how plants convert light energy into chemical energy and how cells break down glucose to produce...",
    objectives: [...mockObjectivesWithProgress.slice(0, 3).map((obj) => ({ ...obj, status: "completed" }))],
    status: "completed",
    onResetProgress: () => console.log("Reset progress 4"),
    onDeleteActivity: () => console.log("Delete activity 4"),
  },
  {
    id: "5",
    title: "Writing Effective Thesis Statements",
    courseName: "English Composition I",
    created: new Date(Date.now() - 3 * 60 * 60 * 1000),
    description: "Learn to craft compelling thesis statements that effectively communicate your main argument and...",
    objectives: [
      {
        id: "1",
        objective: "Identify characteristics of strong thesis statements",
        sequence: 1,
        status: "pending",
      },
      {
        id: "2",
        objective: "Distinguish between weak and strong thesis examples",
        sequence: 2,
        status: "pending",
      },
      {
        id: "3",
        objective: "Write original thesis statements for given topics",
        sequence: 3,
        status: "pending",
      },
    ],
    status: "not_started",
    onResetProgress: () => console.log("Reset progress 5"),
    onDeleteActivity: () => console.log("Delete activity 5"),
  },
  {
    id: "6",
    title: "Introduction to Object-Oriented Programming",
    courseName: "Computer Science 101",
    created: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    description: "Understand the core concepts of OOP including classes, objects, inheritance, and polymorphism...",
    objectives: [
      {
        id: "1",
        objective: "Define classes and create objects",
        sequence: 1,
        status: "completed",
      },
      {
        id: "2",
        objective: "Implement inheritance and method overriding",
        sequence: 2,
        status: "pending",
      },
      {
        id: "3",
        objective: "Apply encapsulation principles",
        sequence: 3,
        status: "pending",
      },
      {
        id: "4",
        objective: "Use polymorphism in practical examples",
        sequence: 4,
        status: "pending",
      },
    ],
    status: "in_progress",
    onResetProgress: () => console.log("Reset progress 6"),
    onDeleteActivity: () => console.log("Delete activity 6"),
  },
  {
    id: "7",
    title: "The American Revolution: Causes and Consequences",
    courseName: "U.S. History",
    created: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
    description: "Analyze the political, economic, and social factors that led to the American Revolution and its...",
    objectives: [
      {
        id: "1",
        objective: "Identify key events leading to the Revolution",
        sequence: 1,
        status: "completed",
      },
      {
        id: "2",
        objective: "Analyze the Declaration of Independence",
        sequence: 2,
        status: "completed",
      },
      {
        id: "3",
        objective: "Evaluate the impact on different social groups",
        sequence: 3,
        status: "completed",
      },
    ],
    status: "completed",
    onResetProgress: () => console.log("Reset progress 7"),
    onDeleteActivity: () => console.log("Delete activity 7"),
  },
];

const CompanionActivityView = () => {
  const { t } = useTranslation();
  const { primaryColor } = useAppConfig();
  const [activeTab, setActiveTab] = useState("active");
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const activitiesWithNavigation = useMemo(
    () =>
      testActivities.map((activity) => ({
        ...activity,
        onPress: () => navigation.navigate("Interactive Learning Environment", { activityId: activity.id }),
      })),
    [navigation]
  );

  // Filter activities by status
  const activeActivities = activitiesWithNavigation.filter((activity) => activity.status !== "completed");
  const completedActivities = activitiesWithNavigation.filter((activity) => activity.status === "completed");

  const tabs = useMemo(
    () => [
      {
        key: "active",
        title: `${t("Active")} (${activeActivities.length})`,
      },
      {
        key: "completed",
        title: `${t("Completed")} (${completedActivities.length})`,
      },
    ],
    [t, activeActivities.length, completedActivities.length]
  );

  const displayedActivities = activeTab === "active" ? activeActivities : completedActivities;

  return (
    <CompanionLayout>
      <View style={styles.container}>
        <WorkspaceHeader title={t("Learning Activities")} />
        <View style={styles.tabWrapper}>
          <TabNavigator
            tabData={tabs}
            activeTab={activeTab}
            onTabPress={(key) => {
              setActiveTab(key);
            }}
            activeTabColor={primaryColor}
            useTranslationText={false}
          />
        </View>
        <ScrollView style={styles.scrollView}>
          <View style={styles.contentContainer}>
            <CompanionILECardList activities={displayedActivities} />
          </View>
        </ScrollView>
      </View>
    </CompanionLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    height: "100%",
    display: "flex",
    // @ts-expect-error - works on web
    maxHeight: "100vh",
    overflow: "hidden",
    backgroundColor: "#f9fafb",
  },
  tabWrapper: {
    marginVertical: 24,
    width: "100%",
    maxWidth: 1400,
    marginHorizontal: "auto",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    width: "100%",
    maxWidth: 1400,
    marginHorizontal: "auto",
  },
});

export default CompanionActivityView;
